import {useMemo} from "react";
import {Select} from "@mantine/core";
import {useTranslation} from "react-i18next";
import type {CollectionDataResponse, ChecklistMetadata, LabelGroup} from "../types";
import {ActionButtons} from "./ActionButtons";
import {CollectionProgress} from "./CollectionProgress";
import styles from "./checklist-config.module.css";

type ChecklistConfigProps = {
    className?: string;
    collectionData?: CollectionDataResponse;
    checklistConfig: {
        metadata: ChecklistMetadata;
        viewGroups: LabelGroup[];
        labelData: Record<string, string>;
        defaultCollectionId: string;
    };
    onPatchDimensions: (dimensions: string[]) => void;
    progress: {max: number; value: number};
    setEditFillingOpen: (open: boolean) => void;
    setCreateFillingOpen: (open: boolean) => void;
    setOpenFillingOpen: (open: boolean) => void;
    setShareFillingOpen: (open: boolean) => void;
    setDuplicateFillingOpen: (open: boolean) => void;
    setDeleteFillingOpen: (open: boolean) => void;
};

export const ChecklistConfig = ({
    className,
    collectionData,
    checklistConfig,
    onPatchDimensions,
    progress,
    setEditFillingOpen,
    setCreateFillingOpen,
    setOpenFillingOpen,
    setShareFillingOpen,
    setDuplicateFillingOpen,
    setDeleteFillingOpen,
}: ChecklistConfigProps) => {
    const {t} = useTranslation();

    const topicName = checklistConfig.metadata.name;
    const viewGroups = checklistConfig.viewGroups;
    const selectedLabels = checklistConfig.labelData;

    const displayTitle =
        collectionData?.metadata?.name ?? t("Info.FillingWithoutName");

    const selectableGroups = useMemo(
        () => viewGroups.filter((g) => g.labels.length > 1),
        [viewGroups],
    );

    return (
        <div className={className}>
            <h2 className={styles.topicName}>{topicName}</h2>
            <div className={styles.fillingData}>
                <h1 className={styles.fillingTitle}>{displayTitle}</h1>
                <ActionButtons
                    collectionData={collectionData}
                    setEditFillingOpen={setEditFillingOpen}
                    setCreateFillingOpen={setCreateFillingOpen}
                    setOpenFillingOpen={setOpenFillingOpen}
                    setShareFillingOpen={setShareFillingOpen}
                    setDuplicateFillingOpen={setDuplicateFillingOpen}
                    setDeleteFillingOpen={setDeleteFillingOpen}
                />
            </div>

            <CollectionProgress progress={progress} />

            {selectableGroups.length > 0 && (
                <div className={styles.comboboxContainer}>
                    {selectableGroups.map((group) => (
                        <Select
                            key={group.id}
                            data={group.labels.map((l) => ({
                                value: l.id,
                                label: l.name,
                            }))}
                            value={selectedLabels[group.id]}
                            onChange={(val) =>
                                val && onPatchDimensions([val])
                            }
                            size="sm"
                            className={styles.combobox}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};
